/**
 * Bulk Upload Framework — Core Types
 *
 * These types define the schema registry, field definitions, relation resolution,
 * validation results, and template structures used throughout the bulk upload system.
 */

// ---------------------------------------------------------------------------
// Field & Schema Definitions
// ---------------------------------------------------------------------------

/** Primitive data types for upload field validation */
export type FieldDataType = 'string' | 'number' | 'date' | 'boolean' | 'enum' | 'relation';

/** Confidence level of a relation match */
export type MatchConfidence = 'exact' | 'high' | 'medium' | 'low' | 'none';

/** How a relation match was determined */
export type MatchType = 'exact' | 'normalized' | 'token' | 'prefix' | 'fuzzy';

/** Defines how a relation field resolves to an ID in the system */
export interface RelationDef {
	/** The entity type being referenced (e.g., 'workPlan', 'entity', 'component') */
	entityType: string;
	/** The display field to match against in the lookup data (e.g., 'name') */
	matchField: string;
	/** The ID field to resolve to (e.g., 'id') */
	idField: string;
	/** Key in the lookupData dictionary (e.g., 'workPlans') */
	contextKey: string;
	/** Additional fields to search against (e.g., 'idExternal', 'code') */
	alternateMatchFields?: string[];
}

/** Constraints for number or string validation */
export interface FieldConstraints {
	min?: number;
	max?: number;
	minLength?: number;
	maxLength?: number;
	pattern?: RegExp;
}

/** A single field definition within an upload schema */
export interface UploadFieldDef {
	/** Internal API field name (e.g., 'name', 'workPlanId') */
	name: string;
	/** Human-readable label (e.g., 'Activity Name', 'Work Plan') */
	label: string;
	/** Primitive data type for validation */
	dataType: FieldDataType;
	/** Is this field required for a valid row? */
	required: boolean;
	/** For 'enum' type: allowed values */
	enumValues?: readonly string[];
	/** For 'relation' type: config for resolving references */
	relation?: RelationDef;
	/** For auto-matching: possible header names in user spreadsheets */
	headerAliases?: string[];
	/** Min/max constraints for number/string types */
	constraints?: FieldConstraints;
	/** Default value if cell is empty */
	defaultValue?: unknown;
	/** Optional description shown in template comments */
	description?: string;
	/** Transform function applied after parsing (e.g., trim, uppercase) */
	transform?: (raw: string) => unknown;
	/** True if this field is a project-defined custom field (values go into customData) */
	isCustomField?: boolean;
	/** The custom column definition ID (e.g., 'col_123456_abc') */
	customFieldId?: string;
}

/** Top-level schema definition for a bulk-uploadable entity type */
export interface UploadSchema {
	/** Unique key (e.g., 'v2Activity', 'v2Condition') */
	entityType: string;
	/** Display name (e.g., 'Activities', 'Conditions') */
	label: string;
	/** API endpoint for batch creation */
	apiEndpoint: string;
	/** HTTP method */
	apiMethod: 'POST' | 'PUT';
	/** Field definitions */
	fields: UploadFieldDef[];
	/** Cross-field validation rules (returns error message or null) */
	crossFieldValidation?: (row: Record<string, unknown>) => string | null;
	/** Maps to CustomFieldEntityType for loading project custom fields (e.g., 'activity', 'work_plan') */
	customFieldEntityType?: string;
	/**
	 * When another schema's relation field references this entity type and
	 * the user wants to create a new record inline during resolution,
	 * use this schema instead of the bulk-upload schema.
	 * Example: v2Contract links → inline creation uses the V1 contract schema.
	 */
	inlineCreationSchema?: UploadSchema;
	/**
	 * Transform form values into the shape the API endpoint expects.
	 * Called just before POSTing in createRelatedRecord.
	 */
	payloadTransform?: (
		formValues: Record<string, unknown>,
		projectId: number
	) => Record<string, unknown>;
	/**
	 * Custom per-row submit function. When provided, the default bulk-create
	 * endpoint is bypassed and each row is submitted through this function.
	 * Used for multi-step flows (e.g., creating a V1 contract then a V2 link).
	 * Returns the created record ID or throws on failure.
	 */
	customSubmit?: (params: {
		row: Record<string, unknown>;
		projectId: number;
		apiBaseUrl: string;
		headers: Record<string, string>;
		signal?: AbortSignal;
	}) => Promise<{ id: number; name?: string }>;
	/**
	 * Within-batch duplicate detection: any two rows that share the same combined
	 * value across these fields will be flagged. The first occurrence is kept clean;
	 * subsequent occurrences receive a row-level error so the user must dedupe
	 * before submit. String values are compared case-insensitively after trim.
	 */
	duplicateKeyFields?: string[];
}

// ---------------------------------------------------------------------------
// Column Mapping
// ---------------------------------------------------------------------------

/** Maps a spreadsheet column to a schema field */
export interface ColumnMapping {
	/** Schema field name this column maps to */
	targetField: string;
	/** Spreadsheet column header (null if unmapped) */
	sourceColumn: string | null;
	/** Whether this mapping was auto-detected */
	autoMatched: boolean;
}

// ---------------------------------------------------------------------------
// Relation Resolution
// ---------------------------------------------------------------------------

/** A scored candidate from the relation resolver */
export interface ScoredCandidate {
	id: number;
	label: string;
	score: number;
	matchType: MatchType;
}

/** Result of scoring a single raw value against lookup data */
export interface RelationScoreResult {
	resolvedId: number | null;
	confidence: MatchConfidence;
	candidates: ScoredCandidate[];
}

/** A group of rows sharing the same unresolved relation value */
export interface ResolutionGroup {
	/** Unique key: fieldName::normalized(rawValue) */
	key: string;
	/** The relation field definition */
	field: UploadFieldDef;
	/** Original value from the spreadsheet */
	rawValue: string;
	/** Scoring result */
	scoreResult: RelationScoreResult;
	/** How many rows have this exact value */
	rowCount: number;
	/** Which row indices (0-based) */
	rowIndices: number[];
	/** User's decision (set interactively on the resolution screen) */
	resolution: ResolutionDecision;
}

/** User's decision for a resolution group */
export interface ResolutionDecision {
	status: 'pending' | 'accepted' | 'manual' | 'skipped' | 'created';
	selectedId: number | null;
	selectedLabel: string | null;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** Row-level validation status */
export type RowStatus = 'valid' | 'warning' | 'error' | 'skipped';

/** A single field-level error */
export interface FieldError {
	field: string;
	message: string;
	value: unknown;
}

/** A single field-level warning */
export interface FieldWarning {
	field: string;
	message: string;
	value: unknown;
}

/** Validation result for a single row */
export interface ValidationResult {
	rowIndex: number;
	status: RowStatus;
	errors: FieldError[];
	warnings: FieldWarning[];
	/** The transformed + resolved data ready for API submission */
	resolvedRow: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Spreadsheet Parsing
// ---------------------------------------------------------------------------

/** Information about a detected context row (label-value pair above data) */
export interface ContextRowEntry {
	label: string;
	value: string;
	labelCol: number;
	valueCol: number;
}

/** A detected context row in the spreadsheet */
export interface ContextRow {
	rowNumber: number;
	entries: ContextRowEntry[];
}

/** Resolved default from a context row, ready for application */
export interface ContextDefault {
	label: string;
	targetField: string;
	rawValue: string;
	/** For primitive fields: the value as-is */
	resolvedValue: unknown;
	/** For relation fields: scoring result */
	relation?: {
		resolvedId: number | null;
		resolvedLabel: string | null;
		confidence: MatchConfidence;
		candidates: ScoredCandidate[];
	};
	/** Whether user confirmed applying this default */
	confirmed: boolean;
}

/** Result of auto-detecting where headers and data are in the worksheet */
export interface HeaderDetection {
	headerRow: number;
	dataStartRow: number;
	confidence: number;
	preHeaderRows: Array<{ rowNumber: number; content: string }>;
}

// ---------------------------------------------------------------------------
// Templates (persisted)
// ---------------------------------------------------------------------------

/** A persisted column mapping entry within a template */
export interface TemplateMappingEntry {
	sourceColumn: string;
	targetField: string;
	transform: string | null;
}

/** Settings persisted with a template */
export interface TemplateSettings {
	sheetName: string | null;
	headerRow: number;
	dataStartRow: number;
	skipEmptyRows: boolean;
	dateFormat: string;
	contextRows?: Array<{
		rowNumber: number;
		defaults: Array<{
			label: string;
			targetField: string;
			value: string;
		}>;
	}>;
}

/** A saved upload template from the DB */
export interface UploadTemplate {
	id: number;
	projectId: number;
	entityType: string;
	name: string;
	description?: string;
	columnMappings: TemplateMappingEntry[];
	settings: TemplateSettings;
	createdBy?: number;
	createdAt?: string;
	updatedBy?: number;
	updatedAt?: string;
	isDefault?: boolean;
}

/** Result of checking a saved template against an uploaded file's headers */
export interface TemplateCompatibility {
	status: 'full-match' | 'partial-match' | 'incompatible';
	matched: ColumnMapping[];
	missing: ColumnMapping[];
	unmapped: string[];
}

// ---------------------------------------------------------------------------
// Bulk Submit
// ---------------------------------------------------------------------------

/** Progress of the bulk upload submission */
export interface UploadProgress {
	status: 'idle' | 'submitting' | 'complete' | 'error';
	totalRows: number;
	processedRows: number;
	successfulRows: number;
	failedRows: number;
	skippedRows: number;
	currentBatch: number;
	totalBatches: number;
	results: BulkRowResult[];
	/** Global error if the entire submission failed */
	error?: string;
}

/** Result for a single row after server response */
export interface BulkRowResult {
	rowIndex: number;
	status: 'created' | 'error' | 'skipped';
	id?: number;
	errors?: string[];
}

/** Combined result of the entire bulk upload operation */
export interface BulkUploadResult {
	totalRows: number;
	successful: number;
	failed: number;
	skipped: number;
	results: BulkRowResult[];
}

// ---------------------------------------------------------------------------
// Lookup Data
// ---------------------------------------------------------------------------

/** Generic lookup item used for relation resolution */
export interface LookupItem {
	id: number;
	name: string;
	[key: string]: unknown;
}

/** Dictionary of lookup data keyed by context key (e.g., 'workPlans', 'entities') */
export type LookupData = Record<string, LookupItem[]>;

// ---------------------------------------------------------------------------
// Hook State
// ---------------------------------------------------------------------------

/** Steps in the bulk upload wizard */
export type BulkUploadStep =
	| 'select-template'
	| 'upload-file'
	| 'map-columns'
	| 'resolve-relations'
	| 'validate-preview'
	| 'submit';

export const BULK_UPLOAD_STEPS: BulkUploadStep[] = [
	'select-template',
	'upload-file',
	'map-columns',
	'resolve-relations',
	'validate-preview',
	'submit',
];

export const STEP_LABEL_KEYS: Record<BulkUploadStep, string> = {
	'select-template': 'bulkUpload.step.selectTemplate',
	'upload-file': 'bulkUpload.step.uploadFile',
	'map-columns': 'bulkUpload.step.mapColumns',
	'resolve-relations': 'bulkUpload.step.resolveRelations',
	'validate-preview': 'bulkUpload.step.validatePreview',
	submit: 'bulkUpload.step.submit',
};
